import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

// Import all components
import WidgetSizeSelector from './components/WidgetSizeSelector';
import ChatBubbleCustomizer from './components/ChatBubbleCustomizer';
import AvatarUploader from './components/AvatarUploader';
import TypographyControls from './components/TypographyControls';
import ColorSchemeCustomizer from './components/ColorSchemeCustomizer';
import TaglineConfigurator from './components/TaglineConfigurator';
import StatusMessagesConfig from './components/StatusMessagesConfig';
import TypingSimulationControls from './components/TypingSimulationControls';
import LivePreviewPanel from './components/LivePreviewPanel';

const AppearanceCustomization = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('widget');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Widget Configuration State
  const [widgetSize, setWidgetSize] = useState('medium');
  const [bubbleIcon, setBubbleIcon] = useState('MessageCircle');
  const [bubbleColor, setBubbleColor] = useState('#21b17a');
  const [botAvatar, setBotAvatar] = useState('');
  const [userAvatar, setUserAvatar] = useState('');

  // Typography State
  const [fontSize, setFontSize] = useState('14');
  const [fontFamily, setFontFamily] = useState('system');

  // Color Scheme State
  const [colorScheme, setColorScheme] = useState({
    primary: '#21b17a',
    primaryHover: '#1a8f65',
    accent: '#3b82f6',
    chatBackground: '#ffffff',
    messageBackground: '#f3f4f6',
    userMessageBackground: '#21b17a',
    textPrimary: '#1f2937',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    divider: '#e5e7eb'
  });

  // Tagline State
  const [showTagline, setShowTagline] = useState(true);
  const [taglineText, setTaglineText] = useState('Wir sind hier, um Ihnen zu helfen!');

  // Status Messages State
  const [onlineMessage, setOnlineMessage] = useState('Wir sind online und bereit zu helfen!');
  const [offlineMessage, setOfflineMessage] = useState('Wir sind momentan offline');

  // Typing Simulation State
  const [enableTyping, setEnableTyping] = useState(true);
  const [typingSpeed, setTypingSpeed] = useState('medium');
  const [showTypingIndicator, setShowTypingIndicator] = useState(true);

  const tabs = [
    {
      id: 'widget',
      label: 'Widget',
      icon: 'Layout',
      description: 'Größe und Bubble-Einstellungen'
    },
    {
      id: 'avatars',
      label: 'Avatare',
      icon: 'User',
      description: 'Bot- und Benutzer-Avatare'
    },
    {
      id: 'typography',
      label: 'Typografie',
      icon: 'Type',
      description: 'Schriftart und -größe'
    },
    {
      id: 'colors',
      label: 'Farben',
      icon: 'Palette',
      description: 'Farbschema anpassen'
    },
    {
      id: 'content',
      label: 'Inhalte',
      icon: 'MessageSquare',
      description: 'Taglines und Status-Nachrichten'
    },
    {
      id: 'behavior',
      label: 'Verhalten',
      icon: 'Settings',
      description: 'Typing-Simulation und Animationen'
    }
  ];

  // Track changes for unsaved indicator
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [
    widgetSize, bubbleIcon, bubbleColor, botAvatar, userAvatar,
    fontSize, fontFamily, colorScheme, showTagline, taglineText,
    onlineMessage, offlineMessage, enableTyping, typingSpeed, showTypingIndicator
  ]);

  const handleSave = () => {
    // Simulate saving configuration
    console.log('Saving appearance configuration...', {
      widgetSize, bubbleIcon, bubbleColor, botAvatar, userAvatar,
      fontSize, fontFamily, colorScheme, showTagline, taglineText,
      onlineMessage, offlineMessage, enableTyping, typingSpeed, showTypingIndicator
    });
    setHasUnsavedChanges(false);
    // Show success message or redirect
  };

  const handleReset = () => {
    if (window.confirm('Möchten Sie alle Änderungen zurücksetzen?')) {
      // Reset to defaults
      setWidgetSize('medium');
      setBubbleIcon('MessageCircle');
      setBubbleColor('#21b17a');
      setBotAvatar('');
      setUserAvatar('');
      setFontSize('14');
      setFontFamily('system');
      setColorScheme({
        primary: '#21b17a',
        primaryHover: '#1a8f65',
        accent: '#3b82f6',
        chatBackground: '#ffffff',
        messageBackground: '#f3f4f6',
        userMessageBackground: '#21b17a',
        textPrimary: '#1f2937',
        textSecondary: '#6b7280',
        textMuted: '#9ca3af',
        border: '#e5e7eb',
        borderLight: '#f3f4f6',
        divider: '#e5e7eb'
      });
      setShowTagline(true);
      setTaglineText('Wir sind hier, um Ihnen zu helfen!');
      setOnlineMessage('Wir sind online und bereit zu helfen!');
      setOfflineMessage('Wir sind momentan offline');
      setEnableTyping(true);
      setTypingSpeed('medium');
      setShowTypingIndicator(true);
      setHasUnsavedChanges(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'widget':
        return (
          <div className="space-y-8">
            <WidgetSizeSelector
              selectedSize={widgetSize}
              onSizeChange={setWidgetSize}
            />
            <ChatBubbleCustomizer
              bubbleIcon={bubbleIcon}
              bubbleColor={bubbleColor}
              onIconChange={setBubbleIcon}
              onColorChange={setBubbleColor}
            />
          </div>
        );
      
      case 'avatars':
        return (
          <AvatarUploader
            botAvatar={botAvatar}
            userAvatar={userAvatar}
            onBotAvatarChange={setBotAvatar}
            onUserAvatarChange={setUserAvatar}
          />
        );
      
      case 'typography':
        return (
          <TypographyControls
            fontSize={fontSize}
            fontFamily={fontFamily}
            onFontSizeChange={setFontSize}
            onFontFamilyChange={setFontFamily}
          />
        );
      
      case 'colors':
        return (
          <ColorSchemeCustomizer
            colorScheme={colorScheme}
            onColorSchemeChange={setColorScheme}
          />
        );
      
      case 'content':
        return (
          <div className="space-y-8">
            <TaglineConfigurator
              showTagline={showTagline}
              taglineText={taglineText}
              onShowTaglineChange={setShowTagline}
              onTaglineTextChange={setTaglineText}
            />
            <StatusMessagesConfig
              onlineMessage={onlineMessage}
              offlineMessage={offlineMessage}
              onOnlineMessageChange={setOnlineMessage}
              onOfflineMessageChange={setOfflineMessage}
            />
          </div>
        );
      
      case 'behavior':
        return (
          <TypingSimulationControls
            enableTyping={enableTyping}
            typingSpeed={typingSpeed}
            showTypingIndicator={showTypingIndicator}
            onEnableTypingChange={setEnableTyping}
            onTypingSpeedChange={setTypingSpeed}
            onShowTypingIndicatorChange={setShowTypingIndicator}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} lg:pb-0 pb-16`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
              <button 
                onClick={() => navigate('/ai-agents-dashboard')}
                className="hover:text-foreground transition-colors"
              >
                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} />
              <span>Appearance Customization</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Erscheinungsbild anpassen
                </h1>
                <p className="text-muted-foreground">
                  Gestalten Sie das Aussehen und Verhalten Ihres Chat-Widgets mit Live-Vorschau
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                {hasUnsavedChanges && (
                  <div className="flex items-center space-x-2 text-warning">
                    <Icon name="AlertCircle" size={16} />
                    <span className="text-sm">Ungespeicherte Änderungen</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={handleReset}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Zurücksetzen
                </Button>
                <Button
                  onClick={handleSave}
                  iconName="Save"
                  iconPosition="left"
                  className={hasUnsavedChanges ? 'animate-pulse' : ''}
                >
                  Speichern
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Configuration Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab Navigation */}
              <div className="border-b border-border">
                <div className="flex flex-wrap gap-1 -mb-px">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        flex items-center space-x-2 px-4 py-3 border-b-2 transition-all duration-200
                        ${activeTab === tab?.id
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/50'
                        }
                      `}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <div className="text-left">
                        <div className="text-sm font-medium">{tab?.label}</div>
                        <div className="text-xs opacity-80 hidden sm:block">{tab?.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="bg-card border border-border rounded-lg p-6">
                {renderTabContent()}
              </div>
            </div>

            {/* Live Preview Panel */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-card border border-border rounded-lg p-6">
                  <LivePreviewPanel
                    widgetSize={widgetSize}
                    bubbleIcon={bubbleIcon}
                    bubbleColor={bubbleColor}
                    botAvatar={botAvatar}
                    userAvatar={userAvatar}
                    fontSize={fontSize}
                    fontFamily={fontFamily}
                    colorScheme={colorScheme}
                    showTagline={showTagline}
                    taglineText={taglineText}
                    onlineMessage={onlineMessage}
                    offlineMessage={offlineMessage}
                    enableTyping={enableTyping}
                    typingSpeed={typingSpeed}
                    showTypingIndicator={showTypingIndicator}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-8 p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-foreground mb-1">Schnellaktionen</h3>
                <p className="text-sm text-muted-foreground">
                  Häufig verwendete Funktionen für die Anpassung
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/german-chatbot')}
                  iconName="Bot"
                  iconPosition="left"
                >
                  Deutscher Chatbot
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/chat-testing-interface')}
                  iconName="MessageSquare"
                  iconPosition="left"
                >
                  Chat testen
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/agent-configuration')}
                  iconName="Settings"
                  iconPosition="left"
                >
                  Konfiguration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppearanceCustomization;